import React, { useRef } from "react";
import clsx from "clsx";
import startCase from "lodash/startCase";
import { useRouter } from "next/router";
import asPage from "components/core/as-page";
import Breadcrumbs from "components/core/breadcrumbs";
import Header from "components/core/header";
import HeaderBanner from "components/core/header-banner";
import Layout from "components/core/layout";
import Accordion from "components/elements/accordion";
import Link from "components/elements/link";
import Svg from "components/elements/svg";
import TextContent from "components/elements/text-content";
import HowItWorksButton from "components/partials/how-it-works-button";
import data from "../../helpers/faq";

const orderedTabs = ["buyer", "seller", "general"];
const tabNames = ["Buyer FAQs", "Seller FAQs", "General FAQs"];

type Tab = keyof typeof data;

const Faq = () => {
  const tabSelectRef = useRef<HTMLSelectElement>(null);
  const router = useRouter();
  const type = (router.query.type?.[0] || orderedTabs[0]) as Tab;
  const faq = data[type];

  const handleSelectTab = async (tab: Tab) => {
    if (orderedTabs.includes(tab)) {
      await router.push(`/faq/${tab}`);
      tabSelectRef.current?.blur();
    }
  };

  const renderTabs = () => {
    return orderedTabs.map((item, index) => {
      const displayName = tabNames[index];

      if (faq.questions.length === 0) {
        return null;
      }

      return (
        <Link
          key={item}
          to={`faq/${item}`}
          className={clsx("faq__menu-item", { active: item === type })}
        >
          {displayName}
          <Svg src="chevron-right" className="icon" />
        </Link>
      );
    });
  };

  const renderTabsContent = () => {
    const { questions } = faq;

    if (questions.length === 0) {
      return null;
    }

    return (
      <div className="Tabs__Content">
        <Accordion multiple={false}>
          {questions.map((item) => (
            <Accordion.Item key={item.question} label={item.question}>
              <TextContent content={item.answer} />
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };

  return (
    <Layout className="faq" title="FAQs">
      <Header>
        <HeaderBanner
          title="FAQs"
          icon="faq"
          background="/static/images/header-faq.jpg"
        />
      </Header>
      <Breadcrumbs>
        <Breadcrumbs.Item title="FAQs" url="faq" />
      </Breadcrumbs>
      <main className="container faq__content">
        <div className="row">
          <div className="hidden-md hidden-lg col-xs-12 faq__menu-dropdown">
            <select
              className="form-control"
              onChange={(e) => handleSelectTab(e.target.value as Tab)}
              value={type}
              ref={tabSelectRef}
            >
              {orderedTabs.map((tab) => (
                <option key={tab} value={tab}>
                  {startCase(tab.toLowerCase())} FAQs
                </option>
              ))}
            </select>
          </div>
          <aside className="hidden-xs hidden-sm col-md-3">
            <nav className="faq__menu">{renderTabs()}</nav>
            <HowItWorksButton />
          </aside>
          <div className="col-xs-12 col-md-9">
            {renderTabsContent()}
            <div className="how-it-works-mobile">
              <HowItWorksButton />
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default asPage(Faq);
